import {
    ApiQuery,
    PaginationQuery,
    SortDirections,
    SortQuery,
} from "~/modules/filters/types";
import { useState } from "react";
import { sortItemsByProperty } from "~/utils/functions/sortItemsByProperty";
import { PlatformTableData, PlatformTableDataColumns } from "../..";

// // // //

/**
 * PLATFORM_SORT_MAP
 * Map each PlatformTableDataColumns to a key used to in-memory sort the array of items using `sortItemsByNestedProperty`
 */
const PLATFORM_SORT_MAP: Partial<Record<PlatformTableDataColumns, string>> = {
    [PlatformTableDataColumns.name]: "name",
    [PlatformTableDataColumns.assetCount]: "asset_count",
    [PlatformTableDataColumns.dailyMintsDollar]:
        "adjusted_daily_mints_dollar.val",
    [PlatformTableDataColumns.bridgedTokenMarketCapDollar]:
        "bridged_token_market_cap_dollar.val",
    [PlatformTableDataColumns.bridgedTokenValueDollar]:
        "bridged_token_value_dollar.val",
    [PlatformTableDataColumns.circulatingAssetValueDollar]:
        "circulating_asset_value_dollar.val",
    [PlatformTableDataColumns.dailyMintsToken]:
        "adjusted_daily_mints_token.val",
    [PlatformTableDataColumns.trailing30DayActiveAddressesCount]:
        "quarterly_active_addresses_count.val",
    [PlatformTableDataColumns.holdingAddressesCount]:
        "holding_addresses_count.val",
    [PlatformTableDataColumns.trailing30DayTransferVolume]:
        "trailing_30_day_transfer_volume.val",
    [PlatformTableDataColumns.id]: "id",
    [PlatformTableDataColumns.slug]: "slug",
    [PlatformTableDataColumns.iconUrl]: "icon_url",
    [PlatformTableDataColumns.colorHex]: "color_hex",
    [PlatformTableDataColumns.description]: "description",
    [PlatformTableDataColumns.tokenizationType]: "tokenization_type",
    [PlatformTableDataColumns.rwaAssetCount]: "rwa_asset_count",
    [PlatformTableDataColumns.stablecoinAssetCount]: "stablecoin_asset_count",
    [PlatformTableDataColumns.distributedMarketCap]:
        "distributed_market_cap.val",
    [PlatformTableDataColumns.representedMarketCap]:
        "represented_market_cap.val",
    [PlatformTableDataColumns.stablecoinMarketCap]: "stablecoin_market_cap.val",
    [PlatformTableDataColumns.rwaHoldersCount]: "rwa_holders_count.val",
    [PlatformTableDataColumns.stablecoinHoldersCount]:
        "stablecoin_holders_count.val",
};

// // // //

export function useInMemoryPlatformSort(props: {
    rows: PlatformTableData[];
    sort?: SortQuery;
    pagination?: PaginationQuery;
}) {
    const {
        rows,
        sort = {
            field: PlatformTableDataColumns.distributedMarketCap,
            direction: SortDirections.desc,
        },
        pagination = {
            page: 1,
            perPage: 25,
        },
    } = props;

    const [query, setQuery] = useState<ApiQuery>({
        sort,
        pagination,
    });

    const sortedItems = sortItemsByProperty({
        items: rows,
        field: PLATFORM_SORT_MAP[query.sort.field as PlatformTableDataColumns],
        direction: query.sort.direction,
        nullsLast: true,
    });

    function setQueryWithDefaultSort(updatedQuery: ApiQuery) {
        const sort =
            updatedQuery.sort.field && updatedQuery.sort.direction
                ? updatedQuery.sort
                : {
                      field: PlatformTableDataColumns.distributedMarketCap,
                      direction: SortDirections.desc,
                  };

        setQuery({
            ...updatedQuery,
            sort,
        });
    }

    return { query, sortedItems, setQuery: setQueryWithDefaultSort };
}
